import { afterNextRender, Component, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { filter } from 'rxjs';
import { AuthService } from './core/services/auth.service';
import { SeoService } from './core/services/seo.service';
import { HeaderComponent } from './shared/components/header/header.component';
import { FooterComponent } from './shared/components/footer/footer.component';
import { SignupPromptModalComponent } from './shared/components/signup-prompt-modal/signup-prompt-modal.component';
import { ToastComponent } from './shared/components/toast/toast.component';

@Component({
  selector: 'app-root',
  imports: [
    RouterOutlet,
    HeaderComponent,
    FooterComponent,
    SignupPromptModalComponent,
    ToastComponent,
  ],
  templateUrl: './app.html',
  styleUrl: './app.scss',
})
export class App {
  private readonly router = inject(Router);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly seo = inject(SeoService);
  private readonly authService = inject(AuthService);
  private readonly destroyRef = inject(DestroyRef);

  constructor() {
    // After the first paint — not in APP_INITIALIZER — so /auth/refresh
    // isn't chained onto LCP behind main.js. Guards call bootstrap()
    // themselves when a route needs the session before that runs.
    afterNextRender(() => {
      this.authService.bootstrap().pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
    });

    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        let route = this.activatedRoute.firstChild;
        while (route?.firstChild) {
          route = route.firstChild;
        }
        const { title, description, lcpImage } = route?.snapshot.data ?? {};
        // 'dynamic' pages (shop, product detail) know their LCP URL only
        // after a fetch — they call setLcpPreload themselves. Clearing here
        // would race a cached response that applied the tag before
        // NavigationEnd.
        if (lcpImage !== 'dynamic') {
          this.seo.setLcpPreload(typeof lcpImage === 'string' ? lcpImage : null);
        }
        if (title) {
          this.seo.set({ title, description });
        }
      });
  }
}
